/**
 * Task Conflict Dialog Component
 *
 * This dialog warns the user when they attempt to add leave time for a mechanic
 * who already has tasks assigned during that period.
 *
 * Purpose:
 * - Prevents accidentally leaving tasks assigned when mechanic is unavailable
 * - Shows list of conflicting tasks for transparency
 * - Allows user to confirm unassignment before proceeding
 *
 * User Flow:
 * 1. User adds sick/vacation/lunch time for mechanic
 * 2. System detects mechanic has assigned tasks during that time
 * 3. This warning dialog appears with list of conflicting tasks
 * 4. User can cancel or proceed with unassignment
 * 5. If confirmed, tasks are unassigned and leave is added
 *
 * @module Calendar/dialogs/TaskConflictDialog
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Task Conflict Warning Dialog
 *
 * Displays a warning when attempting to add leave time for a mechanic
 * who has assigned tasks during the scheduled time.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {string} props.mechanicName - Name of mechanic with task conflicts
 * @param {Array<Object>} props.conflictingTasks - List of tasks that conflict
 * @param {string} props.leaveType - Type of leave (sick, vacation, lunch)
 * @param {Function} props.onClose - Callback when dialog is cancelled
 * @param {Function} props.onConfirm - Callback when user confirms unassignment
 * @returns {React.Element} Task conflict warning dialog
 */
export const TaskConflictDialog = ({
  open,
  mechanicName,
  conflictingTasks = [],
  leaveType = 'leave',
  onClose,
  onConfirm,
}) => {
  const leaveTypeLabel = {
    sick: 'Sick Time',
    vacation: 'Vacation',
    lunch: 'Lunch Break',
  }[leaveType] || 'Leave';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#f57c00' }}>
        <WarningAmberIcon />
        Task Conflict Warning
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>{mechanicName}</strong> has <strong>{conflictingTasks.length}</strong> task
            {conflictingTasks.length !== 1 ? 's' : ''} assigned during this {leaveTypeLabel.toLowerCase()} period:
          </Typography>

          <Box sx={{ maxHeight: '200px', overflowY: 'auto', mb: 2 }}>
            {conflictingTasks.map((task, index) => {
              const startDate = new Date(task.startDate || task.calendar_start || task.appointment_time);
              const timeStr = startDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
              });

              return (
                <Box
                  key={task.task_id || task.id || index}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    backgroundColor: '#fff3e0',
                    borderRadius: 1,
                    border: '1px solid #ffb74d',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {task.service_info || task.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {timeStr} • VIN: {task.vin_number || 'N/A'}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              backgroundColor: '#fff3e0',
              padding: 2,
              borderRadius: 1,
              border: '1px solid #ffb74d',
            }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>What will happen if you proceed:</strong>
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
              <li>These tasks will be moved to <strong>Unassigned</strong></li>
              <li>The {leaveTypeLabel.toLowerCase()} will be added for {mechanicName}</li>
              <li>Tasks will need to be reassigned to another mechanic</li>
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          style={{ backgroundColor: '#f57c00', color: 'white' }}
        >
          Unassign Tasks and Add {leaveTypeLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
